import * as z from "zod";
import { useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import ComboBox from "widgets/shared/inputs/combobox";
import GenericButton from "widgets/controls/buttons/generic";
import { shallowEqual, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import Checkbox from "widgets/shared/inputs/checkbox";
import { SearchInput } from "widgets/shared/inputs/search";

import { usePermissions } from "hooks/usePermissions";
// Schema
const UserSchema = z.object({
  email: z
    .string()
    .min(1, { message: i18next.t("zod.error.required") })
    .email({ message: i18next.t("zod.error.email") }),
  role: z.string().min(1, {
    message: i18next.t("zod.error.required"),
  }),
  classes: z.array(z.string()),
});

export type UserFormType = z.infer<typeof UserSchema>;

// Props
type UserFormProps = {
  submit: (data: UserFormType) => void;
  onClose: () => void;
  defaultValues?: UserFormType;
  values?: UserFormType;
  loading?: boolean;
};

const UserForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
}: UserFormProps) => {
  const form = useForm<UserFormType>({
    resolver: zodResolver(UserSchema),
    defaultValues: {
      email: "",
      role: "",
      classes: [],
    },
    values,
  });

  const { t } = useTranslation();
  const { errors, isDirty } = form.formState;
  const { availableRoles } = usePermissions();
  const sites = useSelector(accountSiteList, shallowEqual);
  const [siteFilter, setSiteFilter] = useState("");

  const filteredSites = useMemo(
    () =>
      sites.filter((site) =>
        site.name.toLowerCase().includes(siteFilter.toLowerCase())
      ),
    [sites, siteFilter]
  );

  const selectAllSites = () => {
    const ids = filteredSites.map((site) => site.id);
    form.setValue("classes", ids, { shouldDirty: true });
  };

  const deselectAllSites = () => {
    form.setValue("classes", [], { shouldDirty: true });
  };

  const onSubmit = async (formData: UserFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const clearErrors = () => {
    form.clearErrors();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className=" space-y-6">
        <div className="grid grid-cols-1 gap-3 ">
          <FormField
            control={form.control}
            name="email"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.email")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.email")}
                    id="user-form-email"
                    type="text"
                    state={errors.email ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="role"
            rules={{ required: true }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.role")}</FormLabel>
                <FormControl>
                  <ComboBox
                    items={availableRoles()}
                    placeholder={t("form.placeholder.role")}
                    id="user-form-role"
                    type="text"
                    state={errors.role ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-3 border-t border-gray-100 pt-4">
            <FormLabel className="text-secondary">
              {t("dialog.user.sites.title")}
            </FormLabel>
            <p className="text-secondary/80 text-txt-sm">
              {t("dialog.user.sites.description")}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2">
                <GenericButton
                  text={"Select All"}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={selectAllSites}
                  rounded="rounded-xl"
                  color="secondary"
                />
                <GenericButton
                  text={"Unselect All"}
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={deselectAllSites}
                  rounded="rounded-xl"
                  color="secondary"
                />
              </div>
              <SearchInput
                onChange={setSiteFilter}
                type="text"
                value={siteFilter}
                sizes="md"
                debounce={300}
              />
            </div>
            <FormField
              control={form.control}
              name="classes"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="max-h-56 w-full overflow-y-auto rounded-xl border border-gray-100 p-2">
                      <div className="grid grid-cols-1 gap-x-1 md:grid-cols-2">
                        {filteredSites.map((item, index) => (
                          <div
                            key={item.id}
                            className={`p-2 ${
                              index % 2 === 1 ? "bg-white" : "bg-gray-50"
                            }`}
                          >
                            <Checkbox
                              id={`add-user-site-${item.id}`}
                              color="secondary"
                              labelKey={item.name}
                              checked={(field.value || []).includes(item.id)}
                              valueKey={item.id}
                              onChange={() => {
                                if ((field.value || []).includes(item.id)) {
                                  field.onChange(
                                    field.value.filter((id) => id !== item.id)
                                  );
                                } else {
                                  field.onChange([...field.value, item.id]);
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <div className="flex gap-2">
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
              color="secondary"
            />

            <GenericButton
              type="submit"
              text={t("button.action.ok")}
              disabled={!isDirty}
              loading={loading || false}
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default UserForm;
